import { memo, useCallback } from 'react';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Button } from 'foundations-components/transitional/components/Button';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useUpdateCampaignContactEngagementAndAssociation } from './hooks/useUpdateCampaignContactEngagementAndAssociation';
import { ContactsCampaignFormProvider, useContactsCampaignFormContext } from './context/ContactsCampaignFormContext';
import ContactsCampaignSelect from './components/ContactsCampaignSelect';
import EngagementSelect from './components/EngagementSelect';
import { StandaloneUsageTracker } from '../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ContactsCampaignAddPanelContent({
  onClose,
  onSuccess,
  editCampaignObjectId
}) {
  const {
    selectedCampaign,
    selectedEngagement,
    existingCampaignData,
    engagementLabel,
    engagementOptions,
    objectId
  } = useContactsCampaignFormContext();
  const isEditMode = Boolean(editCampaignObjectId);
  const {
    save,
    isSaving
  } = useUpdateCampaignContactEngagementAndAssociation({
    objectId,
    onSuccess: () => {
      // Track successful engagement save
      if (selectedCampaign && !isEditMode) {
        StandaloneUsageTracker.track('Campaigns', 'Campaign Contact Association', {
          action: 'contact added to campaign',
          campaignId: selectedCampaign,
          contactId: objectId === null || objectId === void 0 ? void 0 : objectId.toString(),
          engagementType: selectedEngagement === null || selectedEngagement === void 0 ? void 0 : selectedEngagement.text
        });
      } else if (isEditMode && existingCampaignData) {
        var _existingCampaignData;
        // Find previous engagement type from the label
        const previousEngagement = engagementOptions.find(option => option.text === engagementLabel);
        StandaloneUsageTracker.track('Campaigns', 'Campaign Contact Association', {
          action: 'contact campaign association engagement edited',
          campaignId: ((_existingCampaignData = existingCampaignData.properties) === null || _existingCampaignData === void 0 || (_existingCampaignData = _existingCampaignData.hs_origin_asset_id) === null || _existingCampaignData === void 0 ? void 0 : _existingCampaignData.value) || undefined,
          contactId: objectId === null || objectId === void 0 ? void 0 : objectId.toString(),
          engagementType: selectedEngagement === null || selectedEngagement === void 0 ? void 0 : selectedEngagement.text,
          previousEngagementType: previousEngagement === null || previousEngagement === void 0 ? void 0 : previousEngagement.text
        });
      }
      onSuccess === null || onSuccess === void 0 || onSuccess();
      onClose();
    }
  });
  const handleSaveClick = useCallback(() => {
    if (!selectedCampaign && !isEditMode) return;
    if (!selectedEngagement) return;

    // For edit mode, find the current engagement type ID based on the label
    let currentEngagementTypeId;
    if (isEditMode && engagementLabel && engagementOptions.length > 0) {
      const currentEngagement = engagementOptions.find(option => option.text === engagementLabel);
      currentEngagementTypeId = currentEngagement === null || currentEngagement === void 0 ? void 0 : currentEngagement.value;
    }
    void save({
      selectedCampaign,
      selectedEngagement,
      isEditMode,
      existingCampaignData,
      currentEngagementTypeId
    });
  }, [isEditMode, selectedCampaign, selectedEngagement, existingCampaignData, engagementLabel, engagementOptions, save]);
  return /*#__PURE__*/_jsxs(Panel, {
    width: "600px",
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(PanelHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: isEditMode ? 'campaignSelect.editCampaign' : 'campaignSelect.addCampaign'
        })
      })
    }), /*#__PURE__*/_jsxs(PanelBody, {
      padding: "1000",
      children: [/*#__PURE__*/_jsx(ContactsCampaignSelect, {
        editCampaignObjectId: editCampaignObjectId
      }), /*#__PURE__*/_jsx(EngagementSelect, {})]
    }), /*#__PURE__*/_jsxs(PanelFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        onClick: handleSaveClick,
        disabled: !selectedCampaign && !isEditMode || !selectedEngagement,
        loading: isSaving,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.saveEditCampaign"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaignSelect.cancelEditCampaign"
        })
      })]
    })]
  });
}
function ContactsCampaignAddPanel(props) {
  return /*#__PURE__*/_jsx(ContactsCampaignFormProvider, {
    objectType: props.objectType,
    assetId: props.assetId,
    crmIdProperty: props.crmIdProperty,
    editCampaignObjectId: props.editCampaignObjectId,
    children: /*#__PURE__*/_jsx(ContactsCampaignAddPanelContent, Object.assign({}, props))
  });
}
export default /*#__PURE__*/memo(ContactsCampaignAddPanel);